import React, {useContext} from "react"
import styled, {css, keyframes} from "styled-components"
import { getCasinoBackground } from "./casinoBackground";
import dreamsBackground from "../assets/backgrounds/Fondo-AppDreams-2.jpg"
import FullScreenDialog from "./FullScreenDialog"
import Publicidad from "./Publicidad"
import {SiteContext} from "../context/SiteContext";
import Button from "./atoms/Buttons";
import { LuMegaphone } from "react-icons/lu";


export const ContainerBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
  background-image: url(${props => props.$img});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: ${(props) => props.theme.backgroundPosition};

`

const Card = styled.div`
  width: calc(100% - 4rem);
  height: calc(100vh - 4rem);
  background: ${(props) => props.theme.cardBackground};
  border-radius: 24px;
  border: ${(props) => props.theme.cardBorder};
  overflow: hidden;
  transition: all 0.5s ease;
  position: relative;
  z-index: 1;

  /* Resplandor dorado fijo (sin animar). La esfera que recorre el borde es BorderOrb. */
  box-shadow:
    0 0 0 2px rgba(255, 215, 0, 0.3),
    0 14px 34px rgba(255, 215, 0, 0.28),
    inset 0 0 28px rgba(255, 215, 0, 0.42),
    inset 0 1px 2px rgba(255, 255, 255, 0.5);

  @media (max-height: 768px) {
    width: calc(100% - 2rem);
    height: calc(100vh - 2rem);
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  ${props => props.$revealBackground && css`
    background: transparent;
    transition: none;
  `}
`;

// Esfera dorada que recorre todo el contorno de la tarjeta: tarda 6 s en dar la vuelta y vuelve a salir cada 10 s.
//
// Rendimiento (la web queda abierta muchas horas): solo se mueve UNA esfera pequeña con transform y opacity, que
// resuelve la GPU sin repintar nada. Los 4 s de descanso está invisible y sin cambiar ningún valor.
//  - cqw/cqh son relativos a BorderOrbTrack (misma caja que la tarjeta), así que el recorrido se adapta a cualquier pantalla.
//  - El camino sigue la línea central del borde y redondea las esquinas (radio 24 px) con un punto a 45° en cada una.
//  - Los porcentajes de cada tramo suponen una tarjeta de 1856 x 1016 px (16:9 en pantalla completa) para que la velocidad
//    sea casi constante; con otra proporción solo varía un poco la velocidad entre lados.
export const BORDER_ORB_TRAVEL_SECONDS = 6;
export const BORDER_ORB_CYCLE_SECONDS = 10;
const CARD_BORDER = 4; // grosor del borde de la tarjeta (igual en todos los temas de theme.js)
const orbPath = (() => {
  const R = 24, B = CARD_BORDER, I = B / 2, A = R - B, RC = R - I, M = A - RC * Math.SQRT1_2; // línea central, centro de la esquina, punto a 45°
  const W = (off) => `calc(100cqw + ${off}px)`, H = (off) => `calc(100cqh + ${off}px)`;
  const px = (v) => `${+v.toFixed(3)}px`;
  const CHORD = 2 * RC * Math.sin(Math.PI / 8);
  const nodes = [ // [largo del tramo en px (reparte el tiempo), x, y]; origen = esquina interior de la tarjeta
    [0, px(A), px(-I)],
    [1856 - 2 * A, W(-A), px(-I)],                         // borde superior
    [CHORD, W(-M), px(M)], [CHORD, W(I), px(A)],           // esquina superior derecha
    [1016 - 2 * A, W(I), H(-A)],                           // borde derecho
    [CHORD, W(-M), H(-M)], [CHORD, W(-A), H(I)],           // esquina inferior derecha
    [1856 - 2 * A, px(A), H(I)],                           // borde inferior
    [CHORD, px(M), H(-M)], [CHORD, px(-I), H(-A)],         // esquina inferior izquierda
    [1016 - 2 * A, px(-I), px(A)],                         // borde izquierdo
    [CHORD, px(M), px(M)], [CHORD, px(A), px(-I)],         // esquina superior izquierda (cierra la vuelta)
  ];
  const total = nodes.reduce((sum, node) => sum + node[0], 0);
  const travel = (BORDER_ORB_TRAVEL_SECONDS / BORDER_ORB_CYCLE_SECONDS) * 100; // % del ciclo que dura el recorrido
  let accumulated = 0;
  const frames = nodes.map(([length, x, y]) => {
    accumulated += length;
    return `${((accumulated / total) * travel).toFixed(2)}% { transform: translate(${x}, ${y}); }`;
  });
  const fade = 6; // % del ciclo (0,6 s) para aparecer y para desaparecer, mientras ya se está moviendo
  return `
    0% { opacity: 0; }
    ${fade}% { opacity: 1; }
    ${(travel - fade).toFixed(2)}% { opacity: 1; }
    ${travel.toFixed(2)}% { opacity: 0; }
    100% { opacity: 0; transform: translate(${nodes[0][1]}, ${nodes[0][2]}); }
    ${frames.join(' ')}
  `;
})();
const orbTravel = keyframes`${orbPath}`;

// Misma caja que la tarjeta (con su borde de 4 px); solo sirve de referencia de tamaño (cqw/cqh) para el recorrido.
const BorderOrbTrack = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 4rem);
  height: calc(100vh - 4rem);
  border: ${CARD_BORDER}px solid transparent;
  container-type: size;
  pointer-events: none;
  z-index: 2;
  @media (max-height: 768px) {
    width: calc(100% - 2rem);
    height: calc(100vh - 2rem);
  }
  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;
// Esfera difusa del mismo tono que el borde (rgb 250 227 133) para que se funda con él: halo de 28 px y núcleo de 12 px,
// ambos con degradado radial que termina en transparente (sin bordes duros ni sombras, que obligarían a pintar más).
const BorderOrb = styled.span`
  position: absolute;
  top: -14px;
  left: -14px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: radial-gradient(circle closest-side, rgba(255, 232, 130, 0.5) 0%, rgba(250, 227, 133, 0.26) 45%, rgba(250, 227, 133, 0) 100%);
  opacity: 0;
  will-change: transform, opacity;
  animation: ${orbTravel} ${BORDER_ORB_CYCLE_SECONDS}s linear infinite;
  &::after {
    content: '';
    position: absolute;
    top: 8px;
    left: 8px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: radial-gradient(circle closest-side, #fffbe0 0%, #fff0a8 35%, rgba(250, 227, 133, 0.75) 65%, rgba(250, 227, 133, 0) 100%);
  }
`;

const LogoBack = styled.div`
  color: #ffffff80;
  bottom: 4px;
  position: absolute;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 12px;
  transition: all 0.5s ease;
  @media (max-height: 600px) {
    transition: all 0.5s ease;
    font-size: 10px;
  }
`

export const BackgroundHome = styled.div`
  width: 100%;
  height: 100vh;
  overflow: auto;
  flex-wrap: wrap;
  background-image: url(${props => props.$img});
  align-items: center;
  justify-content: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  display: flex;
  background-attachment: fixed;
`

const IconsTop = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 8px;
  color: white;
  top: 48px;
  right: 48px;
  z-index: 2;
  @media (max-height: 600px) {
    transition: all 0.5s ease;
    top: 32px;
    right: 32px;
  }
`

const ButtonPub = styled(Button)`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  padding: 12px 20px;
`

const ContainerBackgroundWrapper = ({children, showBackgroundCard = true, revealBackground = false}) => {

  const isDreams = window.location.host === 'dreams.sorteos.wargos.cloud'

  const {ads, salaId} = useContext(SiteContext)

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Se deriva de ads (no se guarda en estado): los anuncios llegan después de montar la vista y el botón debe aparecer en cuanto llegan.
  const mostrarPub = ads.length > 0

  return (
    <ContainerBackground
      data-casino-background="true"
      $img={isDreams ? dreamsBackground : getCasinoBackground(salaId)}
    >
      <BorderOrbTrack aria-hidden="true" data-testid="border-orb">
        <BorderOrb />
      </BorderOrbTrack>
      <Card $revealBackground={revealBackground}>
        {showBackgroundCard && mostrarPub &&
          <IconsTop>
            <Button variant="outline" aria-label="Mostrar publicidad" title="Mostrar publicidad" onClick={handleClickOpen} style={{ padding: '12px', width: '48px', height: '48px' }}>
              <LuMegaphone size={24} aria-hidden="true" />
            </Button>
          </IconsTop>
        }
        {children}
      </Card>
      <LogoBack>Powered by <strong>Wargos Tec</strong></LogoBack>

      <FullScreenDialog open={open} onClose={handleClose} label="Publicidad">
        <ButtonPub aria-label="Cerrar publicidad" onClick={handleClose}>
          Cerrar
        </ButtonPub>
        <Publicidad/>
      </FullScreenDialog>

    </ContainerBackground>
  )
}

export default ContainerBackgroundWrapper
